import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import {
  Asterisk,
  FormControl,
  FormField,
  FormHelper,
  FormItem,
  FormLabel,
} from '@paid-ui/components/form';
import { RadioGroup, RadioGroupCard } from '@paid-ui/components/radio-group';
import { BusinessType } from '@paid-ui/enums/business';
import { AlertIcon } from '@paid-ui/icons/alert';

import { TermsCheckbox } from '../../components/terms-checkbox';
import { ownerTypeOptions } from '../../constants/business';
import { signUpManager } from '../../models';
import { OwnerType, SignUpStep } from '../../models/enums';
import { resetBusinessFields } from '../../utils/reset-business-fields';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

export const StepOwnerDetails: React.FC<Props> = ({ disabled, goToStep }) => {
  const form = useFormContext();
  const { currentStep, invitedSignUp, invitedBusinessSignUp, partyType } =
    useSnapshot(signUpManager);

  useEffect(() => {
    if (invitedBusinessSignUp) {
      form.setValue('ownerType', OwnerType.BUSINESS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitedBusinessSignUp]);

  const agreedOnTerms = form.watch('agreedOnTerms');
  const ownerType = form.watch('ownerType') as OwnerType;
  const isValid = Boolean(ownerType && agreedOnTerms);

  const handleNext = () => {
    form.setValue('type', BusinessType.OWNER);

    switch (ownerType) {
      case OwnerType.INDIVIDUALS: {
        resetBusinessFields(form);

        if (invitedSignUp && partyType === OwnerType.BUSINESS) {
          goToStep?.(SignUpStep.OWNER_CONFIRM);
        } else {
          goToStep?.(SignUpStep.USER_DETAILS);
        }
        break;
      }
      case OwnerType.BUSINESS: {
        goToStep?.(SignUpStep.BUSINESS_STRUCTURE);
        break;
      }
      default:
    }
  };

  if (currentStep !== SignUpStep.OWNER_DETAILS) {
    return null;
  }

  if (invitedSignUp) {
    switch (partyType) {
      case OwnerType.INDIVIDUALS: {
        return (
          <div className="space-y-9">
            <div className="space-y-3.5">
              <FormField
                name="ownerType"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        {...field}
                        onValueChange={(value) =>
                          form.setValue('ownerType', value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={disabled || invitedBusinessSignUp}
                        className="space-y-3.5"
                      >
                        <RadioGroupCard key={OwnerType.INDIVIDUALS} value={OwnerType.INDIVIDUALS}>
                          Ok, join as an individual
                        </RadioGroupCard>
                        <RadioGroupCard key={OwnerType.BUSINESS} value={OwnerType.BUSINESS}>
                          No, I want to join as a business.
                        </RadioGroupCard>
                        {ownerType === OwnerType.BUSINESS ? (
                          <div className="grid grid-cols-[auto_1fr] gap-x-3.5 rounded-[10px] border border-pink p-5 text-sm text-pink">
                            <AlertIcon className="my-1 h-5 w-5" />
                            <div>
                              You've been invited to join Paid as an individual. Switching to a
                              business sets how you're represented on contracts and can't be changed
                              later.
                            </div>
                          </div>
                        ) : null}
                      </RadioGroup>
                    </FormControl>
                    <FormHelper />
                  </FormItem>
                )}
              />
              <TermsCheckbox />
            </div>
            <Button
              variant="solid"
              disabled={disabled || !isValid}
              className="h-[44px]"
              onClick={handleNext}
            >
              Continue
            </Button>
          </div>
        );
      }
      case OwnerType.BUSINESS: {
        return (
          <div className="space-y-9">
            <div className="space-y-3.5">
              <FormField
                name="ownerType"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        {...field}
                        onValueChange={(value) =>
                          form.setValue('ownerType', value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={disabled || invitedBusinessSignUp}
                        className="space-y-3.5"
                      >
                        <RadioGroupCard key={OwnerType.BUSINESS} value={OwnerType.BUSINESS}>
                          Ok, join as a business.
                        </RadioGroupCard>
                        <RadioGroupCard key={OwnerType.INDIVIDUALS} value={OwnerType.INDIVIDUALS}>
                          No, I want to join as an individual.
                        </RadioGroupCard>
                        {ownerType === OwnerType.INDIVIDUALS ? (
                          <div className="grid grid-cols-[auto_1fr] gap-x-3.5 rounded-[10px] border border-pink p-5 text-sm text-pink">
                            <AlertIcon className="my-1 h-5 w-5" />
                            <div>
                              You've been invited to join Paid as a business. Switching to an
                              individual sets how you're represented on contracts and can't be
                              changed later.
                            </div>
                          </div>
                        ) : null}
                      </RadioGroup>
                    </FormControl>
                    <FormHelper />
                  </FormItem>
                )}
              />
              <TermsCheckbox />
            </div>
            <Button
              variant="solid"
              disabled={disabled || !isValid}
              className="h-[44px]"
              onClick={handleNext}
            >
              Continue
            </Button>
          </div>
        );
      }
      default:
    }
  }

  return (
    <div className="space-y-9">
      <div className="space-y-3.5">
        <FormField
          name="ownerType"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Your role <Asterisk />
              </FormLabel>
              <FormControl>
                <RadioGroup
                  {...field}
                  onValueChange={(value) =>
                    form.setValue('ownerType', value, {
                      shouldValidate: true,
                    })
                  }
                  disabled={disabled || invitedBusinessSignUp}
                  className="space-y-3.5"
                >
                  {ownerTypeOptions.map((option) => (
                    <RadioGroupCard
                      key={option.value}
                      value={option.value}
                      hidden={invitedBusinessSignUp && option.value === OwnerType.INDIVIDUALS}
                    >
                      {option.label}
                    </RadioGroupCard>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
        <TermsCheckbox />
      </div>
      <Button
        variant="solid"
        disabled={disabled || !isValid}
        className="h-[44px]"
        onClick={handleNext}
      >
        Continue
      </Button>
    </div>
  );
};
